(() => {
  if (window.__CHS_UPSELL_ADDON_SCRIPT_LOADED__) return;
  window.__CHS_UPSELL_ADDON_SCRIPT_LOADED__ = true;

  class CHSUpsellAddOn extends HTMLElement {
    constructor() {
      super();

      this.onClick = this.onClick.bind(this);
      this.onChange = this.onChange.bind(this);
      this.onCartUpdated = this.onCartUpdated.bind(this);
    }

    connectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('change', this.onChange);

      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);

      document.removeEventListener('chs:cart-updated', this.onCartUpdated);
      document.removeEventListener('cartUpdated', this.onCartUpdated);
      document.removeEventListener('cart:updated', this.onCartUpdated);

      document.addEventListener('chs:cart-updated', this.onCartUpdated);
      document.addEventListener('cartUpdated', this.onCartUpdated);
      document.addEventListener('cart:updated', this.onCartUpdated);

      this.syncAllSelectedVariantInputs();
      this.syncCheckboxesFromCart();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('change', this.onChange);

      document.removeEventListener('chs:cart-updated', this.onCartUpdated);
      document.removeEventListener('cartUpdated', this.onCartUpdated);
      document.removeEventListener('cart:updated', this.onCartUpdated);
    }

    onCartUpdated() {
      clearTimeout(this.cartSyncTimer);

      this.cartSyncTimer = setTimeout(() => {
        this.syncAllSelectedVariantInputs();
        this.syncCheckboxesFromCart();
      }, 350);
    }

    onClick(event) {
      if (
        event.target.closest(
          '.variant-select, .variant-select-wrapper, .select'
        )
      ) {
        return;
      }

      const item = event.target.closest('.option-item[data-product-id]');
      if (!item || !this.contains(item)) return;
      if (item.dataset.busy === 'true') return;

      const checkbox = item.querySelector('.option-checkbox');
      if (!checkbox) return;

      event.preventDefault();
      event.stopPropagation();

      const shouldAdd = !checkbox.checked;

      checkbox.checked = shouldAdd;
      this.updateItemVisualState(item);

      if (shouldAdd) {
        this.addUpsellToCart(item, checkbox);
      } else {
        this.removeUpsellFromCart(item, checkbox);
      }
    }

    onChange(event) {
      const variantSelect = event.target.closest('.variant-select');
      if (!variantSelect) return;

      const item = variantSelect.closest('.option-item[data-product-id]');
      if (!item) return;

      this.updateVariantVisuals(variantSelect);
      this.ensureHiddenVariantInput(item);

      const checkbox = item.querySelector('.option-checkbox');

      if (checkbox && checkbox.checked) {
        checkbox.checked = false;
        this.updateItemVisualState(item);
        this.removeUpsellFromCart(item, checkbox);
      }
    }

    getShopifyRoot() {
      return window.Shopify &&
        window.Shopify.routes &&
        window.Shopify.routes.root
        ? window.Shopify.routes.root
        : '/';
    }

    dispatchCartSyncEvent(detail = {}) {
      document.dispatchEvent(
        new CustomEvent('chs:cart-updated', {
          bubbles: true,
          detail
        })
      );

      document.dispatchEvent(
        new CustomEvent('cartUpdated', {
          bubbles: true,
          detail
        })
      );

      document.dispatchEvent(
        new CustomEvent('cart:updated', {
          bubbles: true,
          detail
        })
      );
    }

    getProductIdFromItem(item) {
      if (!item || !item.dataset.productId) return null;
      return Number(item.dataset.productId);
    }

    getVariantIdFromItem(item) {
      if (!item) return null;

      const select = item.querySelector('.variant-select');
      if (select && select.value) return Number(select.value);

      const hiddenInput = item.querySelector('.selected-variant-id');
      if (hiddenInput && hiddenInput.value) return Number(hiddenInput.value);

      return null;
    }

    ensureHiddenVariantInput(item) {
      if (!item) return null;

      let hiddenInput = item.querySelector('.selected-variant-id');

      if (!hiddenInput) {
        hiddenInput = document.createElement('input');
        hiddenInput.type = 'hidden';
        hiddenInput.className = 'selected-variant-id';
        item.appendChild(hiddenInput);
      }

      const select = item.querySelector('.variant-select');

      if (select && select.value) {
        hiddenInput.value = select.value;
      }

      return hiddenInput;
    }

    syncAllSelectedVariantInputs() {
      this.querySelectorAll('.option-item[data-product-id]').forEach((item) => {
        this.ensureHiddenVariantInput(item);
      });
    }

    updateItemVisualState(item) {
      const checkbox = item.querySelector('.option-checkbox');
      if (!checkbox) return;

      item.classList.toggle('is-selected', checkbox.checked);
      item.classList.toggle('is-in-cart', checkbox.checked);
      item.setAttribute('aria-pressed', checkbox.checked ? 'true' : 'false');
    }

    updateVariantVisuals(select) {
      const item = select.closest('.option-item[data-product-id]');
      if (!item) return;

      const selectedOption = select.options[select.selectedIndex];
      if (!selectedOption) return;

      const priceWrap = item.querySelector('.option-price');

      if (priceWrap) {
        const price = selectedOption.dataset.price;
        const comparePrice = selectedOption.dataset.comparePrice;

        if (
          price &&
          window.Shopify &&
          typeof Shopify.formatMoney === 'function'
        ) {
          const moneyFormat =
            window.theme?.moneyFormat ||
            window.Shopify?.money_format ||
            '${{amount}}';

          const money = Shopify.formatMoney(price, moneyFormat);

          if (comparePrice && Number(comparePrice) > Number(price)) {
            const compareMoney = Shopify.formatMoney(comparePrice, moneyFormat);

            priceWrap.innerHTML = `<span class="price-current price-item price-item--sale price-item--last">${money}</span><s class="price-compare price--on-sale price-item--regular">${compareMoney}</s>`;
          } else {
            priceWrap.innerHTML = `<span class="price-current price-item price-item--regular">${money}</span>`;
          }
        }
      }

      const hiddenInput = this.ensureHiddenVariantInput(item);
      if (hiddenInput) hiddenInput.value = select.value;

      const imagesJson = item.querySelector('.variant-images-json');
      const imageTarget = item.querySelector(
        '.option-image img, .option-image .variant-image'
      );

      if (imagesJson && imageTarget) {
        try {
          const raw = imagesJson.textContent.trim();
          const imagesMap = raw ? JSON.parse(raw) : {};
          const variantImage = imagesMap[select.value];

          if (variantImage && variantImage.url) {
            imageTarget.src = variantImage.url;
            imageTarget.alt = variantImage.alt || '';
          }
        } catch (error) {
          console.warn('Variant image map parse failed:', error);
        }
      }
    }

    async fetchCart() {
      const response = await fetch(`${this.getShopifyRoot()}cart.js`, {
        headers: {
          Accept: 'application/json'
        },
        credentials: 'same-origin'
      });

      if (!response.ok) {
        throw new Error(`cart.js failed with status ${response.status}`);
      }

      return response.json();
    }

    findMatchingCartItems(cart, productId, variantId) {
      if (!cart || !Array.isArray(cart.items)) return [];

      return cart.items
        .map((cartItem, index) => ({
          item: cartItem,
          line: index + 1
        }))
        .filter(({ item }) => {
          const sameVariant =
            variantId && Number(item.variant_id) === Number(variantId);

          const sameProduct =
            productId && Number(item.product_id) === Number(productId);

          return sameVariant || sameProduct;
        });
    }

    async changeCartLine(line, quantity) {
      const response = await fetch(`${this.getShopifyRoot()}cart/change.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          line: Number(line),
          quantity: Number(quantity)
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data?.description ||
            data?.message ||
            `cart/change.js failed with status ${response.status}`
        );
      }

      this.dispatchCartSyncEvent({
        source: 'upsell-add-on',
        line: Number(line),
        quantity: Number(quantity),
        cart: data
      });

      return data;
    }

    async addVariantToCart(variantId) {
      const response = await fetch(`${this.getShopifyRoot()}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          items: [
            {
              id: Number(variantId),
              quantity: 1
            }
          ]
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data?.description ||
            data?.message ||
            `cart/add.js failed with status ${response.status}`
        );
      }

      this.dispatchCartSyncEvent({
        source: 'upsell-add-on-add',
        variantId: Number(variantId),
        cart: data
      });

      return data;
    }

    async normalizeUpsellQuantity(productId, variantId) {
      let cart = await this.fetchCart();
      let matches = this.findMatchingCartItems(cart, productId, variantId);

      if (!matches.length) return cart;

      for (let index = matches.length - 1; index >= 0; index -= 1) {
        const match = matches[index];

        if (index === 0) {
          if (Number(match.item.quantity) !== 1) {
            await this.changeCartLine(match.line, 1);
          }
        } else {
          await this.changeCartLine(match.line, 0);
        }

        cart = await this.fetchCart();
        matches = this.findMatchingCartItems(cart, productId, variantId);
      }

      this.dispatchCartSyncEvent({
        source: 'upsell-add-on-normalize',
        productId,
        variantId
      });

      return this.fetchCart();
    }

    async removeUpsellProductFromCart(productId, variantId) {
      let cart = await this.fetchCart();
      let matches = this.findMatchingCartItems(cart, productId, variantId);

      for (let index = matches.length - 1; index >= 0; index -= 1) {
        await this.changeCartLine(matches[index].line, 0);
        cart = await this.fetchCart();
        matches = this.findMatchingCartItems(cart, productId, variantId);
      }

      this.dispatchCartSyncEvent({
        source: 'upsell-add-on-remove',
        productId,
        variantId
      });

      return this.fetchCart();
    }

    async addUpsellToCart(item, checkbox) {
      const productId = this.getProductIdFromItem(item);
      const variantId = this.getVariantIdFromItem(item);

      if (!variantId) {
        checkbox.checked = false;
        this.updateItemVisualState(item);
        return;
      }

      item.dataset.busy = 'true';

      try {
        const cart = await this.fetchCart();
        const existingItems = this.findMatchingCartItems(
          cart,
          productId,
          variantId
        );

        if (!existingItems.length) {
          await this.addVariantToCart(variantId);
        }

        await this.normalizeUpsellQuantity(productId, variantId);
        await this.refreshDrawer();
        await this.syncCheckboxesFromCart();

        this.dispatchCartSyncEvent({
          source: 'upsell-add-on-add-complete',
          productId,
          variantId
        });
      } catch (error) {
        checkbox.checked = false;
        this.updateItemVisualState(item);
        console.error('Product upsell add failed:', error);
      } finally {
        item.dataset.busy = 'false';
      }
    }

    async removeUpsellFromCart(item, checkbox) {
      const productId = this.getProductIdFromItem(item);
      const variantId = this.getVariantIdFromItem(item);

      if (!variantId) {
        checkbox.checked = false;
        this.updateItemVisualState(item);
        return;
      }

      item.dataset.busy = 'true';

      try {
        await this.removeUpsellProductFromCart(productId, variantId);
        await this.refreshDrawer();
        await this.syncCheckboxesFromCart();

        this.dispatchCartSyncEvent({
          source: 'upsell-add-on-remove-complete',
          productId,
          variantId
        });
      } catch (error) {
        checkbox.checked = true;
        this.updateItemVisualState(item);
        console.error('Product upsell remove failed:', error);
      } finally {
        item.dataset.busy = 'false';
      }
    }

    async refreshDrawer() {
      const cartDrawer = document.querySelector('cart-drawer');
      if (!cartDrawer) return false;

      const response = await fetch(`${this.getShopifyRoot()}?section_id=cart-drawer`, {
        headers: {
          Accept: 'text/html'
        },
        credentials: 'same-origin'
      });

      if (!response.ok) {
        throw new Error(
          `cart drawer section fetch failed with status ${response.status}`
        );
      }

      const htmlText = await response.text();
      const parsed = new DOMParser().parseFromString(htmlText, 'text/html');
      const newCartDrawer = parsed.querySelector('cart-drawer');

      if (!newCartDrawer) {
        throw new Error('Updated cart-drawer element not found in section response');
      }

      cartDrawer.innerHTML = newCartDrawer.innerHTML;
      cartDrawer.classList.remove('is-empty');

      const overlay = cartDrawer.querySelector('#CartDrawer-Overlay');
      if (overlay && typeof cartDrawer.close === 'function') {
        overlay.addEventListener('click', cartDrawer.close.bind(cartDrawer));
      }

      this.dispatchCartSyncEvent({
        source: 'upsell-add-on-refresh-drawer'
      });

      return true;
    }

    async syncCheckboxesFromCart() {
      try {
        const cart = await this.fetchCart();

        this.querySelectorAll('.option-item[data-product-id]').forEach(
          (item) => {
            const checkbox = item.querySelector('.option-checkbox');
            if (!checkbox) return;

            const productId = this.getProductIdFromItem(item);
            const variantId = this.getVariantIdFromItem(item);
            const matches = this.findMatchingCartItems(
              cart,
              productId,
              variantId
            );

            checkbox.checked = matches.length > 0;
            this.updateItemVisualState(item);

            if (matches.length > 0) {
              this.normalizeUpsellQuantity(productId, variantId).catch(
                (error) => {
                  console.warn('Upsell quantity normalization failed:', error);
                }
              );
            }
          }
        );
      } catch (error) {
        console.warn('Product upsell checkbox sync failed:', error);
      }
    }

    async syncCheckboxForItem(item) {
      try {
        const cart = await this.fetchCart();
        const checkbox = item?.querySelector('.option-checkbox');
        if (!checkbox) return;

        const productId = this.getProductIdFromItem(item);
        const variantId = this.getVariantIdFromItem(item);
        const matches = this.findMatchingCartItems(cart, productId, variantId);

        checkbox.checked = matches.length > 0;
        this.updateItemVisualState(item);

        if (matches.length > 0) {
          await this.normalizeUpsellQuantity(productId, variantId);
        }
      } catch (error) {
        console.warn('Product upsell single checkbox sync failed:', error);
      }
    }

    getSelectedProducts() {
      return [];
    }
  }

  if (!customElements.get('upsell-add-on')) {
    customElements.define('upsell-add-on', CHSUpsellAddOn);
  }

  document.addEventListener(
    'click',
    (event) => {
      const removeButton = event.target.closest(
        [
          'cart-remove-button',
          '.cart-remove-button',
          '.cart-item__remove',
          '.cart-remove',
          'a[href*="/cart/change"]',
          'a[href*="quantity=0"]',
          'button[name="remove"]',
          'button[name="minus"]'
        ].join(',')
      );

      if (!removeButton) return;

      setTimeout(() => {
        document.querySelectorAll('upsell-add-on').forEach((upsell) => {
          if (typeof upsell.syncCheckboxesFromCart === 'function') {
            upsell.syncCheckboxesFromCart();
          }
        });
      }, 500);

      setTimeout(() => {
        document.querySelectorAll('upsell-add-on').forEach((upsell) => {
          if (typeof upsell.syncCheckboxesFromCart === 'function') {
            upsell.syncCheckboxesFromCart();
          }
        });
      }, 1100);

      setTimeout(() => {
        document.querySelectorAll('upsell-add-on').forEach((upsell) => {
          if (typeof upsell.syncCheckboxesFromCart === 'function') {
            upsell.syncCheckboxesFromCart();
          }
        });
      }, 1800);
    },
    true
  );

  window.CHSUpsellAddOn = CHSUpsellAddOn;
})();